import { X, Building2, CreditCard, Package, Calendar, User, Phone, MapPin, Mail } from "lucide-react"
import SallerBank from "../cors/SallerBank";

export default function InvoiceModal({ isOpen, onClose, invoice, isPrintMode = false }) {

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-100" },
      paid: { bg: "bg-green-600", text: "text-white", border: "border-green-600" },
      cancelled: { bg: "bg-red-100", text: "text-red-700", border: "border-red-100" },
      annulé: { bg: "bg-red-100", text: "text-red-700", border: "border-red-100" },
      pending_payment: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-100" },
    }

    const config = statusConfig[status?.toLowerCase()] || {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
    }

    const displayStatus =
      {
        paid: "Réglé",
        cancelled: "Annulé",
        pending_payment: "Paiement en attente",
      }[status?.toLowerCase()] || status

    return (
      <span
        className={`inline-flex items-center px-3 py-[1px] rounded-full text-[10px] font-medium border ${config.bg} ${config.text} ${config.border}`}
      >
        {displayStatus}
      </span>
    )
  }

  const calculateOrderProfit = (order) => {
    if (!order?.order_products) return 0

    let orderProfit = 0

    order.order_products.forEach(product => {
      const unitPrice = Number.parseFloat(product.unit_price || 0)
      const sellingPrice = Number.parseFloat(product.selling_price || 0)
      const quantity = Number.parseInt(product.quantity || 0)

      // Bénéfice par produit
      const productProfit = (sellingPrice - unitPrice) * quantity
      orderProfit += productProfit
    })

    // Soustraction de la livraison
    const deliveryPrice = Number.parseFloat(order.delivery_price || 0)
    orderProfit -= deliveryPrice

    return orderProfit
  }



  if (!isOpen) return null

  if (isPrintMode) {
    return <div className="fixed inset-0 bg-white z-50">{/* <PrintInvoice invoice={invoice} /> */}</div>
  }

  if (!invoice) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center">Facture non trouvée</div>
        </div>
      </div>
    )
  }


  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 print:hidden bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Facture {invoice.invoice_number}</h2>
              <p className="text-xs text-gray-600">Générée le {formatDateTime(invoice.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 cursor-pointer hover:bg-red-400 hover:text-white rounded-full">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[75vh] print:max-h-none print:overflow-visible">
          <div className="p-8 print:p-0 bg-white">
            {/* Header Section */}
            <div className="mb-8 print:mb-6">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-0.5">
                  <h1 className="text-xl font-bold text-gray-900">FACTURE</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium mb-2">N°:</span>
                    <span className="font-mono bg-gray-100 px-3 py-1 text-xs rounded-lg">{invoice.invoice_number}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="mb-4">{getStatusBadge(invoice.status)}</div>
                  <div className="flex text-xs gap-1">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>Date d'émission: </span>
                    </div>
                    <div className="text-gray-900">{formatDateTime(invoice.created_at)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company & Client Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Seller */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl px-6 py-3 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Informations Vendeur</h3>
                </div>
                <div className="space-y-2 grid grid-cols-2">
                  <div className="flex items-start gap-3">
                    <User className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs font-semibold text-gray-900">
                        {invoice.saller?.infos?.name || "N/A"} ({invoice.saller?.infos?.name_store || "N/A"})
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="text-xs w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-700">{invoice.saller?.email || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-700">{invoice.saller?.infos?.phone || "N/A"}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-gray-700">{invoice.saller?.infos?.address || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Banking */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl px-6 py-3 border border-emerald-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CreditCard className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Informations Bancaires</h3>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-2">
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1">Banque</div>
                      <div className="text-xs font-semibold text-gray-900">
                        <SallerBank bank={invoice.saller?.infos?.name_bank} />
                      </div>
                      {invoice.payment_date && (
                        <div className="flex items-center gap-1 mt-1">
                          <div className="text-xs font-medium text-gray-600">Date de paiement:</div>
                          <div className="text-[11px] text-gray-900">{invoice.payment_date}</div>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-600 mb-1 mt-2">RIB</div>
                      <div className="text-xs font-mono text-gray-900">{invoice.saller?.infos?.rib || "N/A"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Orders Summary */}
            {invoice.orders && invoice.orders.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Package className="w-4 h-4 text-purple-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">Détails des Commandes</h3>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="pl-6 pr-3 py-4 text-left text-xs font-semibold text-gray-900">Commande</th>
                          <th className="px-3 py-4 text-center text-xs font-semibold text-gray-900">Produit</th>
                          <th className="px-1 py-4 text-center text-xs font-semibold text-gray-900">Quantité</th>
                          <th className="px-2 py-4 text-center text-xs font-semibold text-gray-900">Prix Unitaire</th>
                          <th className="px-3 py-4 text-center text-xs font-semibold text-gray-900">Prix de Vente</th>
                          <th className="px-3 py-4 text-center text-xs font-semibold text-gray-900">Livraison</th>
                          <th className="px-3 py-4 text-center text-xs font-semibold text-gray-900">Total</th>
                          <th className="px-3 py-4 text-center text-xs font-semibold text-gray-900">Bénéfice</th>
                          <th className="px-3 py-4 text-center text-xs font-semibold text-gray-900">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {invoice.orders.map((order, orderIndex) => {
                          const productCount = order.order_products?.length || 0
                          const orderProfit = calculateOrderProfit(order)

                          return order.order_products?.map((orderProduct, productIndex) => {
                            const unitPrice = Number.parseFloat(orderProduct.unit_price || 0)
                            const sellingPrice = Number.parseFloat(orderProduct.selling_price || 0)
                            const quantity = Number.parseInt(orderProduct.quantity || 0)

                            return (
                              <tr key={`${orderIndex}-${productIndex}`} className="hover:bg-gray-50">
                                {productIndex === 0 ? (
                                  <td className="pl-6 pr-3 py-1 align-top text-left" rowSpan={productCount}>
                                    <div className="space-y-0.5">
                                      <span className="font-mono text-[11px] bg-purple-100 px-2 py-1 rounded text-purple-700">
                                        #{order.code}
                                      </span>
                                      <div className="text-[10px] text-gray-600">{formatDateTime(order.created_at)}</div>
                                    </div>
                                  </td>
                                ) : null}

                                <td className="px-2 py-1">
                                  <div className="text-[11px] text-center font-medium text-gray-900">
                                    {orderProduct.product?.name 
                                      ? (orderProduct.product.name.length > 30 
                                          ? orderProduct.product.name.substring(0, 20) + "..." 
                                          : orderProduct.product.name)
                                      : "N/A"}
                                  </div>
                                </td>

                                <td className="px-1 py-1 text-xs text-center font-medium text-gray-900">{quantity}</td>

                                <td className="px-2 py-1 text-xs text-center text-gray-700">{unitPrice.toFixed(2)} MAD</td>

                                <td className="px-3 py-1 text-xs text-right font-medium text-gray-900">
                                  {sellingPrice.toFixed(2)} MAD
                                </td>

                                {productIndex === 0 ? (
                                  <td className="px-3 py-1 text-xs text-right text-gray-700 align-center" rowSpan={productCount}>
                                    {Number.parseFloat(order.delivery_price || 0).toFixed(2)} MAD
                                  </td>
                                ) : null}

                                {productIndex === 0 ? (
                                  <td className="px-3 py-1 text-xs text-center font-bold text-gray-600 align-center" rowSpan={productCount}>
                                    {Number.parseFloat(order.total || 0).toFixed(2)} MAD
                                  </td>
                                ) : null}

                                {productIndex === 0 ? (
                                  <td className="px-3 py-1 text-xs text-center font-bold text-emerald-600 align-center" rowSpan={productCount}>
                                    {orderProfit.toFixed(2)} MAD
                                  </td>
                                ) : null}

                                {productIndex === 0 ? (
                                  <td className="px-3 py-1 text-xs text-center align-center text-nowrap" rowSpan={productCount}>
                                    {getStatusBadge(order.status)}
                                  </td>
                                ) : null}
                              </tr>
                            )
                          })
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-8">
              <div className="bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-6 border border-green-200">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-xl font-medium text-green-700">Bénéfice Total</div>
                    <div className="text-xs text-gray-600 mt-1">Somme des bénéfices de toutes les commandes</div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">{invoice.benefice.toFixed(2)} MAD</div>
                </div>
              </div>

            </div>

            <div className="text-center text-xs text-gray-500 mt-8 print:hidden">
              Merci d'utiliser notre service. Cette facture est générée automatiquement et n'a pas besoin de signature.
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
